import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Input } from 'components/Input/Input'
import { H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { getOpsEvents } from 'features/operations/opsEventsSlice'
import { IOpsEvent, OpsAlertSeverity, OpsDepartmentKey, OpsTaskSource } from 'types/operationsControl.interface'
import { ITimelineEventGroup, ITimelineTimeFilterOption } from 'types/opsTimeline.interface'
import { DEPARTMENT_FILTERS, matchesTimeFilter, SEVERITY_FILTERS, SOURCE_FILTERS, TIME_FILTERS } from './timelineMeta'
import { TimelineEventRow } from './TimelineEventRow'
import { TimelineGroupRow } from './TimelineGroupRow'
import {
  EmptyStreamWrap,
  FilterBar,
  FilterGroup,
  FilterLabel,
  HeaderRow,
  MarkerDot,
  MarkerLabel,
  MarkerRow,
  SearchWrap,
  StreamLine,
  StreamWrap,
  TapHint,
} from './OpsTimeline.styled'

const GROUP_MIN_SIZE = 3

const buildGroups = (list: IOpsEvent[], grouping: boolean): ITimelineEventGroup[] => {
  if (!grouping) return list.map((e) => ({ key: `single-${e.id}`, time: e.time, events: [e] }))
  const groups: ITimelineEventGroup[] = []
  list.forEach((e) => {
    const current = groups[groups.length - 1]
    const head = current ? current.events[0] : null
    if (head && head.time === e.time && head.department === e.department && head.kind === e.kind) {
      current.events.push(e)
    } else {
      groups.push({ key: `${e.time}-${e.department || 'site'}-${e.kind}-${e.id}`, time: e.time, events: [e] })
    }
  })
  return groups
}

export const OpsTimeline = () => {
  const dispatch = useDispatch<AppDispatch>()
  const apiEvents = useSelector((s: RootState) => s.opsEvents.events)
  const [departmentFilter, setDepartmentFilter] = useState<'ALL' | OpsDepartmentKey>('ALL')
  const [severityFilter, setSeverityFilter] = useState<'ALL' | OpsAlertSeverity>('ALL')
  const [sourceFilter, setSourceFilter] = useState<'ALL' | OpsTaskSource>('ALL')
  const [timeFilter, setTimeFilter] = useState<ITimelineTimeFilterOption['key']>('ALL')
  const [search, setSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<{ [k: string]: boolean }>({})

  useEffect(() => {
    dispatch(getOpsEvents({ page: 1, limit: 100, q: search || undefined }))
  }, [dispatch, search])

  const query = search.trim().toLowerCase()
  const sourceEvents = apiEvents

  const events = useMemo(
    () =>
      sourceEvents
        .filter(
          (e) =>
            (departmentFilter === 'ALL' ||
              e.department === null ||
              e.department === departmentFilter ||
              e.deptTo === departmentFilter) &&
            (severityFilter === 'ALL' ||
              e.severity === severityFilter ||
              (severityFilter === 'info' && e.severity === 'success')) &&
            (sourceFilter === 'ALL' || e.source === null || e.source === sourceFilter) &&
            matchesTimeFilter(timeFilter, e.time) &&
            (!query ||
              e.title.toLowerCase().includes(query) ||
              e.description.toLowerCase().includes(query)),
        )
        .reverse(),
    [sourceEvents, departmentFilter, severityFilter, sourceFilter, timeFilter, query],
  )

  const groups = buildGroups(events, !query)

  const nowLabel = useMemo(() => {
    const now = new Date()
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEvents])

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))

  return (
    <>
      <NavBar />
      <PageWrapper>
        <HeaderRow>
          <H1WithBack title='Shift timeline' arrow />
          <SearchWrap>
            <Input
              nomarg
              search
              noinvalidtext
              placeholder='Search events…'
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </SearchWrap>
        </HeaderRow>
        <OtherText grey>Day Shift · started 06:00 · live</OtherText>
        {!events.length ? (
          <NoData text='No events yet for this shift.' />
        ) : (
          <>
            <FilterBar>
              <FilterGroup>
                <FilterLabel>Department</FilterLabel>
                <TabsContainer>
                  {DEPARTMENT_FILTERS.map((f) => (
                    <Tabs key={f.key} active={departmentFilter === f.key} onClick={() => setDepartmentFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Severity</FilterLabel>
                <TabsContainer>
                  {SEVERITY_FILTERS.map((f) => (
                    <Tabs key={f.key} active={severityFilter === f.key} onClick={() => setSeverityFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Source</FilterLabel>
                <TabsContainer>
                  {SOURCE_FILTERS.map((f) => (
                    <Tabs key={f.key} active={sourceFilter === f.key} onClick={() => setSourceFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Time</FilterLabel>
                <TabsContainer>
                  {TIME_FILTERS.map((f) => (
                    <Tabs key={f.key} active={timeFilter === f.key} onClick={() => setTimeFilter(f.key)}>
                      {f.label}
                    </Tabs>
                  ))}
                </TabsContainer>
              </FilterGroup>
            </FilterBar>
            <TapHint grey>Tap an event to open the task, plan or machine behind it.</TapHint>
            {events.length ? (
              <StreamWrap>
                <StreamLine />
                <MarkerRow>
                  <span />
                  <MarkerDot />
                  <MarkerLabel>Now · {nowLabel}</MarkerLabel>
                </MarkerRow>
                {groups.map((g) =>
                  g.events.length >= GROUP_MIN_SIZE ? (
                    <TimelineGroupRow
                      key={g.key}
                      group={g}
                      expanded={!!expandedGroups[g.key]}
                      onToggle={() => toggleGroup(g.key)}
                    />
                  ) : (
                    <Fragment key={g.key}>
                      {g.events.map((e) => (
                        <TimelineEventRow key={e.id} event={e} />
                      ))}
                    </Fragment>
                  ),
                )}
                <MarkerRow>
                  <span />
                  <MarkerDot $muted />
                  <MarkerLabel $muted>Shift start · 06:00</MarkerLabel>
                </MarkerRow>
              </StreamWrap>
            ) : (
              <EmptyStreamWrap>
                <BodyText grey>No events match these filters.</BodyText>
              </EmptyStreamWrap>
            )}
          </>
        )}
      </PageWrapper>
    </>
  )
}
